// Where the week's pages live (docs/design/SIVUPALSTA.md "Oppitunnit (week hub)"): the lesson is
// the week, and its six steps are sub-routes of it, each ending with "Seuraavaksi".
//
//   /lessons/:id            Tunti (the lesson text)
//   /lessons/:id/cards      Harjoitus (today's cards: Tänään)
//   /lessons/:id/listen     Kuuntele (the week's lines, each: listen, then record)
//   /lessons/:id/speak      Puhu (the focus and its words)
//   /lessons/:id/letter     Kirje (the week's prompt → Kirjeet)
//   /lessons/:id/together   Yhdessä (→ the scene as role play, then Yhteinen sessio)
//
// "Harjoittele viikkoa N" (a passed week's own practice) is built by another branch; it is linked
// here only (weekPracticePath). Pure.
import { WEEK_STEPS, type WeekStep } from '../course/week';

/** The URL slug of each step ('' = the lesson page itself). */
export const STEP_SLUG: Record<WeekStep, string> = {
  lesson: '',
  practice: 'cards',
  listen: 'listen',
  speak: 'speak',
  letter: 'letter',
  together: 'together',
};

export const LESSONS_PATH = '/lessons';

export function lessonPath(id: string): string {
  return `${LESSONS_PATH}/${encodeURIComponent(id)}`;
}

export function stepPath(id: string, step: WeekStep): string {
  const slug = STEP_SLUG[step];
  return slug ? `${lessonPath(id)}/${slug}` : lessonPath(id);
}

/** The step after this one (null after Yhdessä). */
export function nextStep(step: WeekStep): WeekStep | null {
  return WEEK_STEPS[WEEK_STEPS.indexOf(step) + 1] ?? null;
}

/**
 * "Harjoittele viikkoa N": a passed (or open) week's material for practice alone. The route is
 * built in parallel by another branch; until it is merged an unknown sub-route of a lesson shows
 * the lesson itself (routes/Lessons.tsx), so the link always leads somewhere.
 */
export function weekPracticePath(id: string): string {
  return `${lessonPath(id)}/harjoittele`;
}

/** /lessons/week (the old week page) and its #listen / #speak / #letter: the current lesson's step. */
export function oldWeekTarget(currentId: string | null, hash: string): string {
  if (!currentId) return LESSONS_PATH;
  return stepPath(currentId, hash === '#speak' ? 'speak' : hash === '#letter' ? 'letter' : 'listen');
}

// ---------- the guided lesson path (Axel 2026-10-10: app/src/lessonpath/) ----------
// Tunti is a path of parts: the lesson text (/lessons/:id), each of its Kielioppi topics on a page
// of its own (/lessons/:id/lue/:topicId), then the check's start screen (/lessons/:id/koe).

/** One Kielioppi topic of the lesson, read inside the lesson (only that topic's text). */
export function topicPartPath(id: string, topicId: string): string {
  return `${lessonPath(id)}/lue/${encodeURIComponent(topicId)}`;
}

/** The lesson's check (Loppukoe), the path's last part. */
export function testPartPath(id: string): string {
  return `${lessonPath(id)}/koe`;
}

/** A page of a lesson's path (the text, a topic, the check): it opens at the top on a push. */
export function isPathPart(pathname: string): boolean {
  const m = /^\/lessons\/([^/]+)(?:\/lue\/[^/]+|\/koe)?\/?$/.exec(pathname);
  return !!m && m[1] !== 'week';
}
